/*
 * Created on 25.08.2016
 *
 * Copyright(c) 1995 - 2016 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Subject, Subscription, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { LanguageService } from '../../i18n/language.service';
import { LoggingService } from '../../utils/logging.service';
import { I18N } from '../form.i18n';
import { CustomSelectItem } from './custom-select-item';

/**
 * Diese Komponente ermöglicht es mehrere Input-Elemente auszuwählen, z.B.
 * Item 1, 2, und 3. Die Auswahl wird in einem gemeinsamen Input-Feld in Form
 * von kleinen Boxen/Chips dargestellt.
 *
 * Benutzung:
 * <pre>
 * <!-- model is array of selected objects -->
 * <common-form-multiple-input
 *     [(ngModel)]="model"
 *     id="year" title="year" value="year"
 *     [selectableItems]="[{year: '2015'}, {year: '2016'}]"
 *  ></common-form-multiple-input>
 * </pre>
 * oder
 * <pre>
 * <!-- model is array of selected objects -->
 * <common-form-multiple-input
 *     [(ngModel)]="model"
 *     id="idValue" title="titelValue" value="displayName"
 *     [selectableItems]="[
 *         {
 *             idValue: 'myId1',
 *             displayName: 'Display Name 1',
 *             titelValue: 'Mouseover Title 1'
 *         }, {
 *             idValue: 'myId2',
 *             displayName: 'Display Name 2',
 *             titelValue: 'Mouseover Title 2'
 *         }
 *     ]"
 *  ></common-form-multiple-input>
 * </pre>
 * oder
 * <pre>
 * <!-- model is array of id's, e.g. ['myId1']; only this version supports
 *      pre-selected values via array of ids -->
 * <common-form-multiple-input
 *      [(ngModel)]="model"
 *      id="idValue" title="titleValue" value="displayName"
 *      [selectableItems]="[
 *          {
 *               idValue: 'myId1',
 *               displayName: '<not used but must be set>',
 *               titleValue: '<not used but must be set>'
 *          }, {
 *              idValue: 'myId2',
 *              displayName: '<not used but must be set>',
 *              titleValue: '<not used but must be set>'
 *          }
 *      ]"
 *      [mappingItems]="[
 *          {
 *              idValue: 'myId1',
 *              displayName: 'Display Name 1 ## (this will be used!)',
 *              titleValue: 'Titel Value 1 ## (this will be used!)'
 *          }, {
 *              idValue: 'myId2',
 *              displayName: 'Display Name 2 ## (this will be used!)',
 *              titleValue: 'Titel Value 2 ## (this will be used!)'
 *          }
 *      ]"
 * ></common-form-multiple-input>
 * </pre>
 *
 *
 * @author thm
 * @author Valentin Kennke (vake@mms-dresden.de)(valentin.kennke@t-systems.com)
 */

@Component({
    selector: 'common-form-multiple-input',
    templateUrl: 'multiple-input.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultipleInputComponent),
            multi: true
        }
    ]
})
export class MultipleInputComponent implements ControlValueAccessor, OnDestroy, OnChanges, OnInit, AfterViewInit {

    /**
     * Kann 'fluent' oder leer '' sein. Wenn fluentClass gesetzt ist werden die ausgewählten Items
     * nebeneinander angezeigt, sonst untereinander. Default ist '', also untereinander.
     */
    @Input() fluentClass: 'fluent' | '' = ''; // column oriented or not.

    /**
     * Bezeichnet den Name des ID-Feldes der übergebenen items und selectableItems. Werden als items und
     * selectableItems keine MultipleInputItems übergeben stellt dieser Parameter ein Mapping zwischen
     * einem Feld der items auf die ID dar.
     */
    @Input() id: string;

    /**
     * Ob eine id im Model auch dann angezeigt werden soll, wenn sie in den selectableItems
     * nicht mehr vorkommt. Da dann kein "displayValue" ermittelt werden kann, wird stattdessen die id in
     * Klammern dargestellt. Ist diese Option nicht gesetzt, dann wird die ungültige id nicht dargestellt.
     */
    @Input() showInvalidIds: boolean;  // besserer Name bei autocomplete: showInvalidModelValues

    /**
     * Bezeichnet den Name des Value-Feldes der übergebenen items und selectableItems. Werden als items und
     * selectableItems keine MultipleInputItems übergeben stellt dieser Parameter ein Mapping zwischen einem Feld der
     * items auf Value (den Anzeigename) dar.
     */
    @Input() value: string;

    /**
     * Bezeichnet den Name des Title-Feldes der übergebenen items und selectableItems. Werden als items und
     * selectableItems keine MultipleInputItems übergeben stellt dieser Parameter ein Mapping zwischen einem Feld der
     * items auf Title (den Mouseovername) dar.
     */
    @Input() title: string;

    /**
     * Bezeichnet den Name des Checkbox-Feldes der übergebenen items und selectableItems. Werden als items und
     * selectableItems keine MultipleInputItems übergeben stellt dieser Parameter ein Mapping zwischen einem Feld der
     * items auf die Checkbox dar. Ist initial 'checkbox' und mappt daher auf das Property von MultipleInputItems.
     * Übersetzung: Name der Variable, anhand derer entschieden wird, ob die Checkbox de-/selektiert ist.
     */
    @Input() checkbox: string = 'checkbox';

    /**
     * Anzahl an mindestens eingegebenen Zeichen die nötig sind damit die Liste von selectableItems angezeigt werden.
     * In Verbindung mit 'searchStringAvailable' kann somit ein Nachladen der Liste der verfügbaren Optionen erst nach
     * Eingabe von x Zeichen erreicht werden.
     *
     * @param minChars
     */
    // ~~ bewirkt: Umwandlung von string in number, Umwandlung von string in 0 falls der string keine gültige Zahl ist und Abschneiden von
    // evtl. Nachkommastellen da hier nur ganze Zahlen sinnvoll sind
    @Input() set minChars(minChars: string | number) {
        this._minChars = ~~minChars;
    }

    /**
     * Die auswählbaren Items. Werden hier keine Objekte vom Typ MultipleInputItem übergeben muss ein Mapping mit Hilfe
     * der Inputfelder 'id', 'value' und 'title' hergestellt werden. Die selectableItems werden automatisch gefiltert,
     * jenachdem welche items ausgewählt sind.
     *
     * @param selectableItems
     */
    @Input() set selectableItems(selectableItems: MultipleInputItem[] | CustomSelectItem[]) {
        this._selectableItems = selectableItems || [];
        this.filterSelectableItems(this.inputValue);

        // Darstellungswert kann geändert werden. Da müssen items aktuallisieren.
        // @dmpa: Das sieht momentan als ein "dirty hack" aus. Zukünftig soll die ganze Komponente meiner Sicht nach refaktorisieren.
        const idField = this.id || 'id';
        const titleField = this.title || 'title';
        const valueField = this.value || 'value';
        this.items = this.items.map(item => {
            const correspondingSelectedValue = (selectableItems as Array<MultipleInputItem | CustomSelectItem>)
                .find(x => x[idField] === item[idField]);
            if (correspondingSelectedValue) {
                item[titleField] = correspondingSelectedValue[titleField];
                item[valueField] = correspondingSelectedValue[valueField];
            }
            return item;
        });
    }

    /**
     * Wenn true, wird eine Checkbox neben der Anzeige eingeblendet.
     */
    @Input() hasCheckbox: boolean;

    /**
     * Was soll beim Mouseover über der Checkbox angezeigt werden.
     */
    @Input() checkboxLabelTitle: string = '';

    /**
     * Ist der Text für das info icon.
     */
    @Input() dataToolTip: string;

    @Input() modifiedClass = ''; // css class. Wird u.a. zur Anzeige bei geänderten Daten über "modifiedData" benutzt.
    /**
     * Array mit den Items die fürs Mapping verwendet werden sollen,falls als FormControll nur an die IDs der Items
     * gebunden wurde. Wenn es nicht gesetzt ist können in diesen Fall die an ngModel gebundenen Items nicht dargestellt
     * werden.
     */
    @Input() mappingItems: MultipleInputItem[] | CustomSelectItem[];

    /**
     * Wenn die als 'minChars' festgelegte Anzahl von Zeichen eingegeben wurde wird das Event 'searchStringAvailable'
     * mit dem Wert des eingegebenen Strings erzeugt. Die die MultiInputComponent benutzende Komponente kann auf
     * dieses Event reagieren und die 'selectableItems' setzen.
     */
    @Output() searchStringAvailable = new EventEmitter<string>();
    @Output() itemSelect = new EventEmitter();
    @Output() itemRemove = new EventEmitter();

    @ViewChild('searchInput') searchInput: ElementRef;

    isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    disabled: boolean = false;
    filteredItems: any[] = [];
    private _selectableItems: any[] = [];
    private _minChars: number = 0;
    private i18n: any;
    private _items: any[] = [];
    private propagateChange: (_: any) => {};
    private readonly languageSubscription: Subscription;
    private inputValue = '';
    private listFetched: boolean = false;
    private idArrayTemp: string[];
    private clickEventHandler: () => any;
    private ngUnsubscribe$: Subject<void> = new Subject<void>();

    constructor(private languageService: LanguageService,
                private loggingService: LoggingService,
                private renderer: Renderer2,
                private elementRef: ElementRef) {
        this.languageSubscription = languageService.registerForLanguageChanged(this, 'i18n', I18N);
    }

    ngOnInit() {
        this.isOpen$
            .pipe(
                filter(x => x),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(() => this.addClickOuterEventHandler());
    }

    /** Soll die unterstützung von Id-Arrays als Model sicherstellen */
    ngOnChanges(changes: SimpleChanges): void {
        if (this.idArrayTemp && this.mappingItemsExist) {
            const items: any[] = this.mapIdArrayIntoItems(this.idArrayTemp);
            this.idArrayTemp = null;
            this.writeValue(items);
        }
        if (changes.hasOwnProperty('selectableItems') || changes.hasOwnProperty('mappingItems')) {
            // Die Werte sind schon nicht aktuell im "choiceItem", so die Auswahlliste soll geschlossen sein.
            if (this.inputValue) {
                this.isOpen$.next(true);
            }
        }
    }

    ngOnDestroy() {
        if (this.languageSubscription) {
            this.languageSubscription.unsubscribe();
        }
        if (this.clickEventHandler) {
            this.clickEventHandler();
            this.clickEventHandler = undefined;
        }
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngAfterViewInit() {
        if (this.searchInput) {
            fromEvent(this.searchInput.nativeElement, 'click')
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(() => this.showIfNot());
        }
    }

    onKeyboardBlur() {
        this.selectOnInput();
    }

    public set items(items: MultipleInputItem[]) {
        if (!Array.isArray(items)) {
            this.loggingService.error('Die übergebenen items sind nicht vom Typ Array!');
            this.loggingService.error(items);
        } else {
            this._items = items;
            this.filterSelectableItems();
        }
    }

    public get items() {
        return this._items;
    }

    public isCheckbox() {
        return this.hasCheckbox === true;
    }

    hasTooltip() {
        return typeof this.dataToolTip !== 'undefined' && this.dataToolTip.length > 0;
    }

    selectItem(selectableItem: MultipleInputItem) {
        if (this.getId(selectableItem) !== 'no-item') {
            this.inputValue = '';
            this.items.push(selectableItem);
            this.sortItemsAndFilterSelectableItems();
            this.isOpen$.next(false);
            // TODO ngModel oder Angular Form verwenden.
            this.searchInput.nativeElement.value = '';
            this.itemSelect.emit(selectableItem);
            this.doPropagateChange(this.items);
        }
        this.selectOnInput();
    }

    removeItem(event: Event, item: MultipleInputItem) {
        event.preventDefault();
        const index = this.items.indexOf(item);
        if (~index) {
            this.items.splice(index, 1);
            this.sortItemsAndFilterSelectableItems();
            this.itemRemove.emit(item);
            this.doPropagateChange(this.items);
        }
        this.selectOnInput();
    }

    /**
     * Es sollen auch Id-Arrays als Model unterstützt werden.
     * Kommt ein solches Model, muss auf den Input mappingItems gewartet werden um dann
     * das Id-Array in ein Object-Array zu konvertieren (siehe ngOnChanges())
     * Auch beim propagieren der View zum Model muss dann statt eines Objekt-Models ein
     * Id-Array propagiert werden. (siehe doPropagateChange())
     *
     * @param list das Model aus der aufrufenden Komponente
     */
    writeValue(list: string[] | MultipleInputItem[]): void {
        if (this.checkIfIdArray(list)) {
            if (this.mappingItemsExist) {
                this.items = this.mapIdArrayIntoItems(list as string[]);
                this.sortItemsAndFilterSelectableItems();
            } else {
                this.idArrayTemp = list as string[];
                this.items = [];
            }
        } else {
            this.items = list as MultipleInputItem[] || [];
            this.sortItemsAndFilterSelectableItems();
        }
    }

    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    registerOnTouched(_: any): void {
        // do nothing
    }

    setDisabledState(isDisabled: boolean): void {
        // Ohne Timeout kommt 'Expression has changed after it was checked.'
        setTimeout(() => this.disabled = isDisabled);
    }

    /**
     * Wird bei verändertem Wert des Input-Feldes aufgerufen. Die auswählbaren Items werden je nach Wert des Input-Feldes gefiltert und die
     * Liste der auswählbaren Items wird angezeigt.
     *
     * @param value der eingegebene Wert
     */
    onInput(value: string) {
        this.searchStringAvailable.emit(value);
        const oldVal = this.inputValue;
        this.inputValue = value;
        if (this._minChars > 0) {
            // Wenn minChars gesetzt ist werden die möglichen Items über fetchList nachgeladen und mit jeder weiteren Eingabe gefiltert
            if (value.length === this._minChars) {
                this.fetchList();
            } else if (value.length > this._minChars) {
                if (oldVal.substring(0, this._minChars) === value.substring(0, this._minChars)) {
                    this.filterSelectableItems(value);
                    this.listFetched = true;
                } else {
                    this.fetchList();
                }
            } else if (value.length < this._minChars) {
                if (this._selectableItems.length > 0) {
                    this.selectableItems = [];
                    this.listFetched = false;
                }
                this.filterSelectableItems(value);
            }
        } else {
            // Wenn minChars nicht gesetzt ist dann wird nur mit den initial übergebenen Items gefiltert
            this.filterSelectableItems(value);
            this.listFetched = true;
        }
        this.isOpen$.next(true);
    }

    getTitle(obj: MultipleInputItem) {
        return this.getVal(obj, 'title', this.title);
    }

    // getCheckbox(obj: MultipleInputItem) {
    //     return this.getVal(obj, 'checkbox', this.checkbox);
    // }

    showIfNot() {
        if (!this.isOpen$.getValue()) {
            this.isOpen$.next(true);
        }
    }

    getId(obj: MultipleInputItem) {
        return this.getVal(obj, 'id', this.id);
    }

    getValue(obj: MultipleInputItem): string {
        return this.getVal(obj, 'value', this.value);
    }

    private checkIfIdArray(value: any[]): boolean {
        if (value && value instanceof Array) {
            let somethingIsNotStringAndNumber = false;
            value.forEach(item => {
                if (typeof item !== 'string' && typeof item !== 'number') {
                    somethingIsNotStringAndNumber = true;
                }
            });
            return !somethingIsNotStringAndNumber;
        }
    }

    /** Soll die unterstützung von Id-Arrays als Model sicherstellen */
    private doPropagateChange(items: any[]) {
        if (this.mappingItemsExist) {
            this.propagateChange(items.map(item => item[this.id || 'id']));
        } else {
            // items-Array mittels Spread-Operation klonen, da sonst die Änderungen nicht im Modell aktualisiert würden,
            // wenn die Input-Komponente sich in einem Formly-Formular befindet. Vermutlich wegen ChangeDetection von Formly
            this.propagateChange([...items]);
        }
    }

    private mapIdArrayIntoItems(idArray: string[]): MultipleInputItem[] {
        const resolvedItems: any[] = [];
        idArray.forEach(id => {
            const item = (this.mappingItems as Array<MultipleInputItem | CustomSelectItem>)
                .find(mappingItem => id === mappingItem[this.id || 'id']) ||
                // Sofern es das bereits früher selektierte Item nicht mehr in der Liste möglicher Items gibt,
                // konstruieren wir eins, damit der Nutzer es zumindest sieht und sauber löschen kann.
                this.showInvalidIds && {
                    [this.id || 'id']: id,
                    [this.value || 'value']: '(' + id + ')',
                    [this.title || 'title']: '(' + id + ')'
                };
            if (item) {
                resolvedItems.push(item);
            }
        });
        return resolvedItems;
    }

    private get mappingItemsExist(): boolean {
        return this.mappingItems && this.mappingItems.length > 0;
    }

    private sortItemsAndFilterSelectableItems() {
        this.sortItems();
        this.filterSelectableItems();
    }

    /**
     * Sortiert die ausgewählten Items alphabetisch nach ihrem Anzeigename.
     */
    private sortItems() {
        this.items.sort((item1: any, item2: any) => this.getValue(item1).localeCompare(this.getValue(item2)));
    }

    private fetchList() {
        this.listFetched = true;
    }

    /**
     * Filtert die Liste der auswählbaren Items. Der übergebene Wert muss an beliebiger Stelle in dem Anzeigewert des Items vorkommen, damit
     * es weiterhin in der Liste enthalten ist. Groß- und Kleinschreibung wird dabei ignoriert. Weiterhin werden bereits ausgewählte Items
     * aus der Liste ausgeschlossen.
     *
     * @param value der Wert des Eingabefeldes
     */
    private filterSelectableItems(value: string = '') {
        this.filteredItems = this._selectableItems.filter(item => this.getValue(item).toLowerCase().indexOf(value.toLowerCase()) > -1);
        this.filteredItems = this.filteredItems.filter(selectableItem =>
            this.items.findIndex(item => this.itemsEqual(item, selectableItem)) === -1);
        if (this.filteredItems.length === 0) {
            if (this._minChars && value.length < this._minChars && !this.listFetched) {
                this.filteredItems.push(
                    new MultipleInputItem(
                        'no-item',
                        this.i18n.multipleInputComponent.notEnoughChars.replace('{minChars}', this._minChars)
                    )
                );
            } else {
                this.filteredItems.push(
                    new MultipleInputItem(
                        'no-item',
                        this.i18n.multipleInputComponent.noResult,
                    )
                );
            }
        }
    }

    private itemsEqual(item1: MultipleInputItem, item2: MultipleInputItem) {
        return this.getId(item1) === this.getId(item2);
    }

    /**
     * Liefert den Wert eines bestimmten Feldes des übergebenen Objekts. Zuerst wird geprüft ob das Objekt ein Feld mit dem Namen des
     * Parameterwertes 'fieldName' hat. Ist das der Fall wird der Wert dieses Feldes zurückgegeben. Andernfalls sollte ein Mapping
     * existieren, d.h. der Parameter 'mappingName' sollte einen Wert haben der dem Name eines Feldes des Objekts obj entspricht. Ist dies
     * der Fall wird dessen Wert zurückgegeben, anderfalls wird null zurückgegeben und eine Warnung auf die Konsole geschrieben.
     *
     * @param obj das Objekt
     * @param fieldName der Name des Feldes
     * @param mappingName der Name des Feldes
     * @returns den Wert des Feldes oder null falls nicht vorhanden
     */
    private getVal(obj: MultipleInputItem, fieldName: string, mappingName: string): string {
        if (obj.id === 'no-item') {
            return obj[fieldName];
        }

        if (mappingName) {
            if (obj.hasOwnProperty(mappingName)) {
                const res: string | number = obj[mappingName];
                if (typeof res === 'string') {
                    return res;
                } else if (typeof res === 'number') {
                    return res.toString();
                } else {
                    this.loggingService.warn('Der zurückgegebene Wert:');
                    this.loggingService.warn(res);
                    throw new Error('Der ermittelte Wert ist nicht vom Type string oder number.');
                }
            } else if (~mappingName.indexOf('.')) {
                if (obj.hasOwnProperty(mappingName.substring(0, mappingName.indexOf('.')))) {
                    const reducedMappingName = mappingName.substring(mappingName.indexOf('.') + 1);
                    return this.getVal(obj[mappingName.substring(0, mappingName.indexOf('.'))], reducedMappingName, reducedMappingName);
                }
            }
        } else if (obj.hasOwnProperty(fieldName)) {
            const res = obj[fieldName];
            if (typeof res === 'string') {
                return res;
            } else {
                this.loggingService.warn('Der zurückgegebene Wert:');
                this.loggingService.warn(res);
                throw new Error('Der ermittelte Wert ist nicht vom Type string.');
            }
        }
        this.loggingService.debug('Das übergebene Objekt:');
        this.loggingService.debug(obj);
        throw new Error('Das übergebene Objekt hat weder ein Feld ' + fieldName + ' noch ein Attribut ' + mappingName + '. ' +
            'Evtl. wurde kein oder ein falsches Attributmapping spezifiziert?');
    }

    private addClickOuterEventHandler() {
        if (this.clickEventHandler) {
            this.clickEventHandler();
            this.clickEventHandler = undefined;
        }
        setTimeout(() => {
            this.clickEventHandler = this.renderer.listen('document', 'click', e => {
                if (!this.elementRef.nativeElement.contains(e.target)) {
                    this.isOpen$.next(false);
                }
            });
        });
    }

    private selectOnInput(): void {
        if (this.searchInput) {
            this.searchInput.nativeElement.focus();
        }
    }
}

/**
 * Repräsentiert die Struktur der Objekte die als items und selectableItems der MultipleInputComponent übergeben werden können.
 *
 * @author Valentin Kennke (vake@mms-dresden.de)(valentin.kennke@t-systems.com)
 */
export class MultipleInputItem {

    public id: string;
    public value: string;
    public title: string;
    public checkbox: string;

    /**
     * Konstruktor.
     *
     * @param id eindeutige ID
     * @param value der Anzeigenname des Items
     * @param title der Mouseovername des Items. Wird kein Wert übergeben entspricht der title dem value.
     * @param checkbox ist die Checkbox gewählt? Default ist false
     */
    constructor(id: string, value: string, title?: string, checkbox?: string) {
        this.id = id;
        this.value = value;
        this.title = title || this.value;
        this.checkbox = checkbox || 'false';
    }
}



